import React, { useState } from 'react';
import { matchCategoryRule } from '../services/categorizationRules';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { dbService } from '../services/dbService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiPlus, FiMinus, FiExpand, FiCalendar } = FiIcons;

const QuickAddForm = ({ onClose }) => {
  const { state, actions } = useApp();
  const { categories, settings } = state;
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastAutomaticCategory, setLastAutomaticCategory] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'expense',
    amount: '',
    category: '',
    payee: '',
    notes: '',
    tags: '',
    account: 'Main Account',
    person: '',
  });

  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const currentCategories = formData.type === 'expense' ? expenseCategories : incomeCategories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.category || isSubmitting) {
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const transaction = {
        ...formData,
        amount: parseFloat(formData.amount),
        status: 'posted',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Add to database and get the ID
      const transactionId = await dbService.addTransaction(transaction);
      
      // Add the ID to the transaction object and update the state
      const transactionWithId = {
        ...transaction,
        id: transactionId
      };
      
      actions.addTransaction(transactionWithId);
      onClose();
    } catch (error) {
      console.error('Failed to add transaction:', error);
      setSubmitError('Failed to add transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'category') setLastAutomaticCategory('');
    if (field === 'payee') {
      const rule = matchCategoryRule({payee:value,type:formData.type}, settings.categorizationRules || [], categories);
      if (rule && (!formData.category || formData.category === lastAutomaticCategory)) {
        setLastAutomaticCategory(rule.category);
        setFormData(prev => ({...prev,payee:value,category:rule.category}));
        return;
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-select first category when type changes
    if (field === 'type') {
      setLastAutomaticCategory('');
      const newCategories = value === 'expense' ? expenseCategories : incomeCategories;
      setFormData(prev => ({ ...prev, category: '' }));
    }
  };

  const activeRule = matchCategoryRule({payee:formData.payee,type:formData.type}, settings.categorizationRules || [], categories);

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.2 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className={`quick-add-panel bg-white rounded-2xl shadow-xl w-full max-w-md ${
          isExpanded ? 'max-h-[90vh] overflow-y-auto mobile-modal-body' : 'mobile-modal-body'
        }`}
        onClick={(e) => e.stopPropagation()}
        layout={!settings.reducedMotion}
      >
        {/* Header */}
        <div className="quick-add-header shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quick Add Transaction</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors duration-200"
              title={isExpanded ? 'Collapse' : 'Expand'}
              type="button"
            >
              <SafeIcon icon={FiExpand} className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors duration-200"
              type="button"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="quick-add-fields p-4 space-y-4">
          {/* Date Field */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Date
            </label>
            <div className="quick-add-date-wrapper relative w-full min-w-0">
              <div className="quick-add-date-display flex items-center justify-between gap-3 w-full min-w-0 rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900">
                <span className="flex items-center gap-3 min-w-0">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 shrink-0 text-gray-500" />
                  <span className="truncate">{formData.date ? new Date(formData.date + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Select date'}</span>
                </span>
                <FiIcons.FiChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
              <input
                type="date"
                aria-label="Transaction Date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="quick-add-date absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer"
                disabled={isSubmitting}
                required
              />
            </div>
          </div>

          {/* Type and Amount */}
          <div className="flex min-w-0 gap-2">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => handleChange('type', 'expense')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  formData.type === 'expense'
                    ? 'bg-danger-500 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiMinus} className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleChange('type', 'income')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  formData.type === 'income'
                    ? 'bg-success-500 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              </button>
            </div>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              className="flex-1 min-w-0 w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Merchant is visible so categorisation can work in Quick Add. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payee
                </label>
                <input
                  type="text"
                  placeholder="Who did you pay or receive from?"
                  value={formData.payee}
                  onChange={(e) => handleChange('payee', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            >
              <option value="">Select category</option>
              {currentCategories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
              {activeRule && <p className="text-xs text-blue-600 mt-1" role="status">Merchant rule: {activeRule.pattern} → {activeRule.category}. You can change the category manually.</p>}
          </div>

          {/* Notes (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <input
              type="text"
              placeholder="Optional notes about this transaction"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Expanded fields */}
          {isExpanded && (
            <motion.div
              className="space-y-4"
              initial={settings.reducedMotion ? {} : { opacity: 0, height: 0 }}
              animate={settings.reducedMotion ? {} : { opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
            >


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  placeholder="Comma-separated tags (e.g., food, restaurant, lunch)"
                  value={formData.tags}
                  onChange={(e) => handleChange('tags', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account
                </label>
                <input
                  type="text"
                  placeholder="Which account was used?"
                  value={formData.account}
                  onChange={(e) => handleChange('account', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Person
                </label>
                <input
                  type="text"
                  placeholder="Associated person (if any)"
                  value={formData.person}
                  onChange={(e) => handleChange('person', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>
          )}

          {submitError && <p role="alert" className="text-sm text-rose-600">{submitError}</p>}
          {/* Actions */}
          <div className="quick-add-actions flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg font-medium transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.amount || !formData.category || isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adding...
                </div>
              ) : (
                'Add Transaction'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default QuickAddForm;